import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Download, Layers, ListChecks, PieChart as PieIcon } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, money, num, fmtDayMonth,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, TotalsCheck,
  DateRange, Segmented, RefreshButton, CHART, axisMoney, MoneyTooltip, codeLabel, marginLabel, periodLabel,
} from '../financeKit';

// Owner shares are categories, not states, so they need telling apart — muted tints of one family.
const OWNER_COLOURS = ['#4f46e5', '#64748b', '#818cf8', '#94a3b8', '#3730a3', '#cbd5e1', '#a5b4fc', '#475569'];

const GROUPS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const n = (v) => parseFloat(v || 0) || 0;
const sum = (rows, key) => rows.reduce((s, r) => s + n(r[key]), 0);
const pct = (profit, revenue) => (n(revenue) ? `${num((n(profit) / n(revenue)) * 100, 1)}%` : '—');

function downloadCSV(filename, headers, rows) {
  const lines = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Net profit dots: primary for a profit, red for a loss, so a losing period stands out on the line.
function ProfitDot({ cx, cy, payload }) {
  if (cx == null || cy == null) return null;
  const loss = n(payload?.net_profit) < 0;
  return <circle cx={cx} cy={cy} r={3.5} fill={loss ? CHART.loss : CHART.profit} stroke="#fff" strokeWidth={1.5} />;
}

export default function ProfitReport() {
  const [raw, setRaw]     = useState(null);
  const [group, setGroup] = useState('week');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();
  const [from, setFrom] = useState(() => isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/profit?from=${from}&to=${to}&group=${group}`);
      setRaw(r.data || null);
    } catch (e) {
      setRaw(null);
      setError(e.response?.data?.message || "We couldn't load the profit report. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to, group]);

  useEffect(() => { load(); }, [load]);

  const data       = Array.isArray(raw) ? raw : (raw?.timeline || []);
  const byType     = raw?.by_shift_type || [];
  const ownerSplit = (raw?.owner_splits || []).map(o => ({
    name: o.name,
    amount: parseFloat(o.amount || 0),
    percentage: parseFloat(o.percentage || 0),
  }));

  // Every total is the plain sum of the rows the server sent, so the tiles, the totals row and the
  // export can never disagree with each other.
  const totRevenue  = sum(data, 'revenue');
  const totPay      = sum(data, 'staff_cost');
  const totSuper    = sum(data, 'super_amount');
  const totFuel     = sum(data, 'fuel');
  const totExpenses = sum(data, 'expenses');
  const totProfit   = sum(data, 'net_profit');
  const totShifts   = sum(data, 'shifts');
  const totHours    = sum(data, 'hours');

  const typeShifts  = sum(byType, 'shifts');
  const typeRevenue = sum(byType, 'revenue');
  const typeProfit  = sum(byType, 'profit');
  const ownerTotal  = ownerSplit.reduce((s, o) => s + o.amount, 0);

  // The server also sends its own totals; if the rows ever stop adding up to them, say so.
  const checks = raw?.totals ? [
    { label: 'Revenue', computed: totRevenue, expected: raw.totals.revenue },
    { label: 'Expenses', computed: totExpenses, expected: raw.totals.expenses },
    { label: 'Net profit', computed: totProfit, expected: raw.totals.net_profit },
  ] : [];

  function exportCSV() {
    const headers = ['Period','Shifts','Hours','Revenue','Staff Cost','Fuel','Expenses','Net Profit','Margin %'];
    const rows = data.map(row => {
      const m = row.revenue ? ((row.net_profit / row.revenue) * 100) : 0;
      return [row.period, row.shifts, Number(row.hours || 0).toFixed(1),
        Number(row.revenue || 0).toFixed(2), Number(row.staff_cost || 0).toFixed(2),
        Number(row.fuel || 0).toFixed(2), Number(row.expenses || 0).toFixed(2),
        Number(row.net_profit || 0).toFixed(2), m.toFixed(1)];
    });
    downloadCSV(`profit-${from}-to-${to}.csv`, headers, rows);
    toast.success(`Exported ${data.length} period${data.length === 1 ? '' : 's'}`);
  }

  const noData = !loading && !error && data.length === 0;

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Profit and loss"
        description="Revenue, costs and net profit for approved, non-cancelled shifts, by period."
        actions={(
          <>
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="profit" />
            <Segmented ariaLabel="Group by" options={GROUPS} value={group} onChange={setGroup} />
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
            <button type="button" onClick={exportCSV} disabled={data.length === 0} className="btn-secondary">
              <Download size={14} /> Export CSV
            </button>
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Summary — Revenue − staff − expenses and fuel = net profit, to the cent. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Revenue" value={loading ? '…' : money(totRevenue)} accent="slate"
              sub={loading ? null : `${num(totShifts)} shifts · ${num(totHours, 1)} h`} />
            <KpiTile label="Staff pay and super" value={loading ? '…' : money(totPay + totSuper)} accent="slate"
              sub={loading ? null : `Pay ${money(totPay)} · super ${money(totSuper)}`} />
            <KpiTile label="Expenses and fuel" value={loading ? '…' : money(totExpenses + totFuel)} accent="slate"
              sub={loading ? null : `Expenses ${money(totExpenses)} · fuel ${money(totFuel)}`} />
            <KpiTile label="Net profit" value={loading ? '…' : money(totProfit)}
              accent="primary" alert={!loading && totProfit < 0}
              sub={loading ? null : marginLabel(totProfit, totRevenue)} />
          </div>

          <TotalsCheck checks={checks} />

          {/* Chart — every cost that affects profit is on it, so a losing period shows why. */}
          <SectionCard title="By period" icon={BarChart3} bodyClass="p-4">
            {loading ? (
              <TableSkeleton rows={5} cols={4} />
            ) : noData ? (
              <EmptyState icon={BarChart3} title="No approved shifts in this range"
                hint="Profit is counted once a shift's timesheet is approved. Pick a wider range, or approve timesheets first." />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: CHART.axis }} tickLine={false}
                      tickFormatter={(p) => (/^\d{4}-\d{2}$/.test(String(p)) ? periodLabel(p, group) : fmtDayMonth(p))} />
                    <YAxis tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={axisMoney} width={56} tickLine={false} axisLine={false} />
                    <Tooltip content={<MoneyTooltip labelFormatter={(p) => periodLabel(p, group)} />} cursor={{ fill: 'rgba(148,163,184,.12)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine y={0} stroke="#cbd5e1" />
                    <Bar dataKey="revenue" name="Revenue" fill={CHART.revenue} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="staff_cost" name="Staff pay" stackId="cost" fill={CHART.staff} maxBarSize={28} />
                    <Bar dataKey="super_amount" name="Super" stackId="cost" fill={CHART.super} maxBarSize={28} />
                    <Bar dataKey="fuel" name="Fuel" stackId="cost" fill={CHART.fuel} maxBarSize={28} />
                    <Bar dataKey="expenses" name="Expenses" stackId="cost" fill={CHART.expenses} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Line type="linear" dataKey="net_profit" name="Net profit" stroke={CHART.profit} strokeWidth={2}
                      dot={<ProfitDot />} activeDot={{ r: 5 }} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="text-xs text-slate-500 mt-2">
                  The cost bar stacks staff pay, super, fuel and expenses. Net profit is revenue minus all four; a red dot is a loss.
                </p>
              </>
            )}
          </SectionCard>

          {/* Shift type breakdown + owner split */}
          {!loading && (byType.length > 0 || ownerSplit.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {byType.length > 0 && (
                <SectionCard title={`Revenue by shift type · ${byType.length}`} icon={Layers}>
                  <div className={TABLE_WRAP}>
                    <table className={TABLE}>
                      <thead>
                        <tr className={THEAD_ROW}>
                          <th scope="col" className={TH}>Shift type</th>
                          <th scope="col" className={TH_NUM}>Shifts</th>
                          <th scope="col" className={TH_NUM}>Revenue</th>
                          <th scope="col" className={TH_NUM}>Shift profit</th>
                          <th scope="col" className={TH_NUM}>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byType.map((row, i) => (
                          <tr key={`${row.shift_type}-${i}`} className={TR}>
                            <td className={TD}>
                              <span className="font-medium text-slate-800" title={row.shift_type || ''}>{codeLabel(row.shift_type) || 'Standard'}</span>
                            </td>
                            <td className={TD_NUM}>{num(row.shifts)}</td>
                            <td className={TD_NUM}><MoneyCell value={row.revenue} /></td>
                            <td className={TD_NUM}><MoneyCell value={row.profit} /></td>
                            <td className={TD_NUM}>{pct(row.profit, row.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={TFOOT_ROW}>
                          <td className={TD}>Total</td>
                          <td className={TD_NUM}>{num(typeShifts)}</td>
                          <td className={TD_NUM}><MoneyCell value={typeRevenue} strong /></td>
                          <td className={TD_NUM}><MoneyCell value={typeProfit} strong /></td>
                          <td className={TD_NUM}>{pct(typeProfit, typeRevenue)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="px-4 py-2.5 text-xs text-slate-500 border-t border-slate-100">
                    Shift profit is revenue less staff pay, super and fuel for each shift, before business expenses.
                  </p>
                </SectionCard>
              )}

              {ownerSplit.length > 0 && (
                <SectionCard title="Owner profit split" icon={PieIcon} bodyClass="p-4">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={ownerSplit} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                        dataKey="amount" nameKey="name" paddingAngle={2} isAnimationActive={false}>
                        {ownerSplit.map((_, i) => <Cell key={i} fill={OWNER_COLOURS[i % OWNER_COLOURS.length]} />)}
                      </Pie>
                      <Tooltip content={<MoneyTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className={TABLE_WRAP}>
                    <table className={`${TABLE} mt-3`}>
                      <thead>
                        <tr className={THEAD_ROW}>
                          <th scope="col" className={TH}>Owner</th>
                          <th scope="col" className={TH_NUM}>Share</th>
                          <th scope="col" className={TH_NUM}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ownerSplit.map((o, i) => (
                          <tr key={`${o.name}-${i}`} className={TR}>
                            <td className={TD}>
                              <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: OWNER_COLOURS[i % OWNER_COLOURS.length] }} aria-hidden="true" />
                              <span className="font-medium text-slate-800">{o.name}</span>
                            </td>
                            <td className={TD_NUM}>{num(o.percentage, 1)}%</td>
                            <td className={TD_NUM}><MoneyCell value={o.amount} /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={TFOOT_ROW}>
                          <td className={TD}>Total</td>
                          <td className={TD_NUM}>{num(ownerSplit.reduce((s, o) => s + o.percentage, 0), 1)}%</td>
                          <td className={TD_NUM}><MoneyCell value={ownerTotal} strong /></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Each owner's share of the net profit for the range.</p>
                </SectionCard>
              )}
            </div>
          )}

          {/* Detail table — its totals row equals the tiles above. */}
          <SectionCard title={`Detail by period${loading ? '' : ` · ${data.length}`}`} icon={ListChecks}>
            {loading ? (
              <TableSkeleton rows={6} cols={8} />
            ) : noData ? (
              <EmptyState icon={ListChecks} title="Nothing to show for this range"
                hint="Rows appear once shifts in the range have approved timesheets." />
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Period</th>
                      <th scope="col" className={TH_NUM}>Shifts</th>
                      <th scope="col" className={TH_NUM}>Hours</th>
                      <th scope="col" className={TH_NUM}>Revenue</th>
                      <th scope="col" className={TH_NUM}>Staff pay</th>
                      <th scope="col" className={TH_NUM}>Super</th>
                      <th scope="col" className={TH_NUM}>Fuel</th>
                      <th scope="col" className={TH_NUM}>Expenses</th>
                      <th scope="col" className={TH_NUM}>Net profit</th>
                      <th scope="col" className={TH_NUM}>Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.period} className={TR}>
                        <td className={`${TD} whitespace-nowrap font-medium text-slate-800`}>{periodLabel(row.period, group)}</td>
                        <td className={TD_NUM}>{num(row.shifts)}</td>
                        <td className={TD_NUM}>{num(row.hours, 1)}</td>
                        <td className={TD_NUM}><MoneyCell value={row.revenue} /></td>
                        <td className={TD_NUM}><MoneyCell value={row.staff_cost} /></td>
                        <td className={TD_NUM}><MoneyCell value={row.super_amount} /></td>
                        <td className={TD_NUM}><MoneyCell value={row.fuel} /></td>
                        <td className={TD_NUM}><MoneyCell value={row.expenses} /></td>
                        <td className={TD_NUM}><MoneyCell value={row.net_profit} strong /></td>
                        <td className={TD_NUM}>{pct(row.net_profit, row.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD}>Total</td>
                      <td className={TD_NUM}>{num(totShifts)}</td>
                      <td className={TD_NUM}>{num(totHours, 1)}</td>
                      <td className={TD_NUM}><MoneyCell value={totRevenue} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={totPay} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={totSuper} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={totFuel} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={totExpenses} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={totProfit} strong /></td>
                      <td className={TD_NUM}>{pct(totProfit, totRevenue)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
